import { zodResolver } from '@hookform/resolvers/zod'
import { SignInInput } from '@numistic/contracts'
import { useSignIn } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Alert, Button, Field, Input } from '@numistic/ui'
import { createRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { AuthCard } from '../../components/auth-card.js'
import { useErrorText } from '../../lib/errors.js'
import { loadSession, safeRedirect } from '../../lib/guards.js'
import { rootRoute } from '../root.js'

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ context, search }) => {
    if (await loadSession(context)) throw redirect({ to: safeRedirect(search.redirect) })
  },
  component: LoginPage,
})

function LoginPage() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const search = loginRoute.useSearch()
  const navigate = useNavigate()
  const signIn = useSignIn()
  const form = useForm({
    resolver: zodResolver(SignInInput),
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = form.handleSubmit((values) =>
    signIn.mutate(values, {
      onSuccess: () => void navigate({ to: safeRedirect(search.redirect) }),
    }),
  )

  return (
    <AuthCard title={t('auth.signInTitle')}>
      <form className="grid gap-4" onSubmit={onSubmit} noValidate>
        {signIn.error ? (
          <Alert variant="destructive">{errorText.request(signIn.error)}</Alert>
        ) : null}
        <Field label={t('auth.email')} error={errorText.field(form.formState.errors.email)}>
          <Input type="email" autoComplete="username" autoFocus {...form.register('email')} />
        </Field>
        <Field label={t('auth.password')} error={errorText.field(form.formState.errors.password)}>
          <Input type="password" autoComplete="current-password" {...form.register('password')} />
        </Field>
        <Button type="submit" disabled={signIn.isPending}>
          {t('auth.signInSubmit')}
        </Button>
        <Link
          to="/forgot-password"
          className="text-center text-sm text-muted-foreground hover:text-foreground"
        >
          {t('auth.forgotLink')}
        </Link>
      </form>
    </AuthCard>
  )
}
